import { Loader2, Server, Shield } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import type { AccessSnapshot, GrantSnapshot } from '@/types/snapshot'
import { useI18n } from '@/i18n'
import { isMaxInstant, formatDateShort } from '@/lib/date-utils'
import {EmptyState} from '@/components/shared/EmptyState'

interface AccessSnapshotViewProps {
    data: AccessSnapshot | undefined
    isLoading: boolean
    error: Error | null
}

function groupByTargetSystem(grants: GrantSnapshot[]): Map<string, GrantSnapshot[]> {
    const grouped = new Map<string, GrantSnapshot[]>()
    for (const grant of grants) {
        const existing = grouped.get(grant.targetSystemName) || []
        existing.push(grant)
        grouped.set(grant.targetSystemName, existing)
    }
    return grouped
}

export function AccessSnapshotView({ data, isLoading, error }: AccessSnapshotViewProps) {
    const i18n = useI18n()

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{i18n.accessSnapshot.loadingAccessState}</span>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-destructive">
                <p>{i18n.accessSnapshot.failedToLoad}</p>
                <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
            </div>
        )
    }

    if (!data || data.grants.length === 0) {
        return (
            <EmptyState
                icon={Shield}
                title={String(i18n.accessSnapshot.noAccessGrants)}
            />
        )
    }

    const groupedGrants = groupByTargetSystem(data.grants)

    return (
        <div className="space-y-6">
            <div className="text-sm text-muted-foreground">
                {i18n.accessSnapshot.grantsActive({ count: data.totalGrants })}
            </div>

            {Array.from(groupedGrants.entries()).map(([systemName, grants]) => (
                <div key={systemName} className="border rounded-lg">
                    <div className="flex items-center gap-2 p-3 bg-muted/50 border-b">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{systemName}</span>
                        <Badge variant="outline" className="ml-auto">
                            {i18n.accessSnapshot.grantCount({ count: grants.length })}
                        </Badge>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-resource">{i18n.common.resource}</TableHead>
                                <TableHead data-testid="col-entitlement">{i18n.common.entitlement}</TableHead>
                                <TableHead data-testid="col-operation">{i18n.common.operation}</TableHead>
                                <TableHead data-testid="col-valid-until">{i18n.common.validUntil}</TableHead>
                                <TableHead data-testid="col-source">{i18n.accessTimeline.source}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {grants.map((grant) => (
                                <TableRow key={grant.accessId}>
                                    <TableCell className="font-medium">
                                        {grant.resourceName}
                                    </TableCell>
                                    <TableCell>{grant.enforcementObjectName}</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary">{grant.operation}</Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {isMaxInstant(grant.validTo) ? i18n.common.permanent : formatDateShort(grant.validTo)}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {grant.sources.map((source) => (
                                                <Badge
                                                    key={source.assignmentId}
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {source.accessProfileName}
                                                </Badge>
                                            ))}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ))}
        </div>
    )
}
